import { useConstant } from '@rcm/react-utils';
import {
  MESSAGE_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED,
  MESSAGE_ERROR_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED,
} from '@reliv/cart';
import Axios from 'axios';
import type { CreditCardData, LegacyCartFormMessage } from 'cart-ui-react';
import {
  CreditCardFields,
  CreditCardForm,
  getCreditCardData,
  useCreditCardFieldsState,
} from 'cart-ui-react';
import { Button } from 'forms';
import t from 'rcm-i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { default as Chase3dsManager } from './Chase3dsManager';
import type { Transaction } from './types';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCartPaymentInfo,
  selectIsEditing,
} from 'cart-ui-react/src/autoshipState/selectors';
import CartV2CreditCardFields from '/cart-ui-react/src/container/CartV2/components/CartV2CreditCardFields';
import { getCheckoutOrderManager } from 'cart-ui';
import {
  setEditingAlert,
  setSnackbar,
} from 'cart-ui-react/src/autoshipState/autoshipSlice';

interface Props {
  order: any;
  onSubmit?: (data: { type: string; data: CreditCardData }) => void;
  messages: LegacyCartFormMessage[];
  paymentType: string;
  isCartV2?: boolean;
  disabled?: boolean;
  setError?: any;
}

/**
 * The purpose of this is to cut down on bot spam to the error log http endpoint.
 * This is not a totally secret code since its in the client's codebase.
 */
const errorLoggingClientAuthCode =
  'zWufneVAd9FeUTco3FJ5mcZMWp9zJ1dQyB3zvEsnkzr8huRXJEXcSBnIm5GEvS9T';
const chase3dsContainerElementId = 'chase3dsThirdPartyUiContainer';

const PaymentFormCreditCardChase3ds = ({
  order,
  onSubmit,
  messages,
  paymentType,
  isCartV2 = false,
  disabled = false,
  setError,
}: Props) => {
  const creditCardFieldsState = useCreditCardFieldsState();
  const dispatch = useDispatch();
  const [waitingFor3ds, setWaitingFor3ds] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const cartV2PaymentInfo = useSelector(selectCartPaymentInfo);
  // const paymentInfo = isCartV2 ? cartV2PaymentInfo : getCreditCardData(creditCardFieldsState);
  const isEditing = useSelector(selectIsEditing);

  const handleEditingAlert = () => {
    if (isCartV2 && isEditing) {
      dispatch(setEditingAlert(true));
      dispatch(
        setSnackbar({
          message: t('Please save any changes before you proceed.'),
          type: 'warning',
          delay: 4500,
        })
      );
      setTimeout(() => {
        dispatch(setEditingAlert(false));
      }, 100);
      return;
    }
  };

  const handleAuthComplete = useCallback(
    (transaction: Transaction) => {
      window.rcmLoading.setLoading('PaymentFormCreditCardChase3ds', 100);
      setWaitingFor3ds(false);
      getCheckoutOrderManager().then(async (orderManager) => {
        await orderManager
          .processCurrentOrder({
            data: {
              // chase3dsOrderId: transaction.orderId,
              // chase3dsTransactionId: transaction.transactionId,
              ...transaction.cardData,
            },
            type: paymentType,
          })
          .then(() => {
            const paymentError = JSON.parse(
              localStorage.getItem('paymentError')
            );

            if (paymentError) {
              dispatch(setError(paymentError[0].value));
              localStorage.removeItem('paymentError');
            }
          });
      });
      Axios.post('/chase3ds/logFromBrowser', {
        action: 'authCompleteFromBrowser',
        authorization: errorLoggingClientAuthCode,
        message: 'handleAuthComplete() was called',
        details: { message: 'No further details.' },
      });
    },
    [onSubmit, paymentType]
  );

  const handleGatewaySaysDoNotProceed = useCallback((details: Object) => {
    Axios.post('/chase3ds/logFromBrowser', {
      action: 'doNotProceedFromBrowser',
      authorization: errorLoggingClientAuthCode,
      message: 'handleGatewaySaysDoNotProceed() was called',
      details,
    });
    window.rcmLoading.setLoading('PaymentFormCreditCardChase3ds', 100);
    if (isCartV2) {
      setError(MESSAGE_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED);
    } else {
      setErrorMessage(MESSAGE_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED);
    }
    setWaitingFor3ds(false);
  }, []);

  const handleAuthenticationNetworkError = useCallback((error: Error) => {
    Axios.post('/chase3ds/logFromBrowser', {
      action: 'errorFromBrowser',
      authorization: errorLoggingClientAuthCode,
      message: 'handleAuthenticationNetworkError() was called',
      details: { message: error.message },
    });
    window.rcmLoading.setLoading('PaymentFormCreditCardChase3ds', 100);
    if (isCartV2) {
      setError(MESSAGE_ERROR_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED);
    } else {
      setErrorMessage(MESSAGE_ERROR_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED);
    }
    setWaitingFor3ds(false);
  }, []);

  const chase3dsManager = useConstant(
    () =>
      new Chase3dsManager(
        handleAuthComplete,
        handleGatewaySaysDoNotProceed,
        handleAuthenticationNetworkError
      )
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const paymentInfo = isCartV2
        ? {
            cardNumber: cartV2PaymentInfo.cardNumber,
            nameOnCard: cartV2PaymentInfo.nameOnCard,
            expirationMonth: Number(cartV2PaymentInfo.expirationMonth),
            expirationYear: Number(cartV2PaymentInfo.expirationYear),
            verificationCode: cartV2PaymentInfo.verificationCode,
          }
        : getCreditCardData(creditCardFieldsState);

      // Filter out any chars that are not numeric digits. Non numeric digits cause JPM Chase to throw errors.
      const cardNumber = isCartV2
        ? paymentInfo.cardNumber.replace(/\D/g, '')
        : creditCardFieldsState.cardNumber.replace(/\D/g, '');
      setErrorMessage('');
      setWaitingFor3ds(true);
      window.rcmLoading.setLoading('PaymentFormCreditCardChase3ds', 0);
      const chase3dsVersion = localStorage.getItem('chaseVersion') ?? '66';
      chase3dsManager.beginTransaction(
        chase3dsContainerElementId,
        getCreditCardData({ ...paymentInfo, cardNumber }),
        chase3dsVersion
      );
    },
    [chase3dsManager, creditCardFieldsState, cartV2PaymentInfo]
  );

  useEffect(() => {
    chase3dsManager?.handlePossiblePreviousAuthAttempt();
    /* Example DDC Initial JWT Request */
    /*
    Axios.get('/api/v2/cardinal/jwt/ddc')
    .then(res => {
      console.log('JWT:', res.data.jwt);
    })
    .catch(err => {
      console.error('Error:', err.response?.data || err.message);
    })
    */

    /* Example cardinal config request */
    /*
    Axios.get('/api/v2/cardinal/config')
    .then(res => {
      console.log('config:', res.data.config);
    })
    .catch(err => {
      console.error('Error:', err.response?.data || err.message);
    })
    *.

    /* Example 3ds challenge JWT Request */
    /*
   Axios.post('/api/v2/cardinal/jwt/authenticate', {
      acsUrl: 'https://issuer.com/acs',
      payload: 'eJxVjz...', // Replace with actual Base64 challenge request
      transactionId: 'a1b2c3d4-e5f6-7890-abcd-1234567890ef',
    })
    .then(res => {
      console.log('Received JWT:', res.data.jwt);
    })
    .catch(err => {
      console.error('Error:', err.response?.data || err.message);
    })
   */
  }, [chase3dsManager]);

  return (
    <div className='chase3dsPaymentFormWrapper'>
      {!!errorMessage && (
        <div className='alert alert-warning'>{t(errorMessage)}</div>
      )}
      <CreditCardForm onSubmit={handleSubmit} cartV2={isCartV2}>
        <CreditCardForm.Body>
          {isCartV2 ? (
            <CartV2CreditCardFields />
          ) : (
            <CreditCardFields
              {...creditCardFieldsState}
              disabled={disabled}
              messages={messages}
              order={order}
            />
          )}
        </CreditCardForm.Body>
        <CreditCardForm.Foot>
          {waitingFor3ds || disabled || isEditing ?
            <Button
              css={{ opacity: '0.7' }}
              btn='primary'
              className='continueButton'
              onClick={handleEditingAlert}
              text={t('Place Order')}
            />
          :
            <div>
              <Button
                btn='primary'
                className='continueButton'
                disabled={waitingFor3ds || disabled || isEditing}
                text={t('Place Order')}
                type='submit'
              />
            </div>
          }

        </CreditCardForm.Foot>
      </CreditCardForm>
    </div>
  );
};

export default PaymentFormCreditCardChase3ds;
